import type { BeforeSendFn } from "posthog-js";
import type { AutocaptureElement } from "./types";

const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_SEGMENT = /^\d+$/;

const URL_PROPERTIES = ["$current_url", "$referrer"] as const;
const DROPPED_PROPERTIES = ["$el_text", "$elements_chain"] as const;

export const normalizePathname = (pathname: string): string =>
  pathname
    .split("/")
    .map((segment) =>
      UUID_SEGMENT.test(segment) || NUMERIC_SEGMENT.test(segment)
        ? ":id"
        : segment,
    )
    .join("/");

export const normalizeUrl = (raw: string): string => {
  try {
    const url = new URL(raw);

    return `${url.origin}${normalizePathname(url.pathname)}`;
  } catch {
    return raw;
  }
};

/**
 * Route ids are normalized to `:id` so path analysis groups by screen rather
 * than by record - without this every contact is its own node in the funnel.
 * Query strings and autocapture element text are dropped because inbox and
 * contact surfaces put customer names in both.
 */
export const scrubEvent: BeforeSendFn = (event) => {
  if (!event) return event;

  const { properties } = event;

  if (!properties) return event;

  for (const key of URL_PROPERTIES) {
    if (typeof properties[key] === "string") {
      properties[key] = normalizeUrl(properties[key]);
    }
  }

  if (typeof properties.$pathname === "string") {
    properties.$pathname = normalizePathname(properties.$pathname);
  }

  for (const key of DROPPED_PROPERTIES) {
    delete properties[key];
  }

  if (Array.isArray(properties.$elements)) {
    properties.$elements = (properties.$elements as AutocaptureElement[]).map(
      ({ text: _text, ...rest }) => rest,
    );
  }

  return event;
};
